import compact from "lodash/compact";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import moment from "moment";
import {
  isAvailabilityRuleV2,
  isDateWithRangesValueV2,
  isDateWithSingleRangeValueV2,
  isFilterValueIntegerRangeV2,
  isFilterValueOptionV2,
  isOptionsFilterV2
} from "PFCore/helpers/filters";
import { FilterOptionV2, FilterV2, NumberFilterV2 } from "PFTypes/filters_v2";

type getFilterV2DisplayValueArgs = {
  filter: FilterV2;
  formatDate: (rawDate: any) => string;
};

export const getFilterV2DisplayValue = ({ filter, formatDate }: getFilterV2DisplayValueArgs): string => {
  const { title, value } = filter;

  if (isNil(value)) {
    return "";
  }

  if (isFilterValueIntegerRangeV2(value)) {
    const min = (filter as NumberFilterV2).statistics?.min ?? value.gte ?? 0;
    const max = (filter as NumberFilterV2).statistics?.max ?? value.lte ?? 0;
    const valueMin = Math.max(value.gte ?? min, min);
    const valueMax = Math.min(value.lte ?? max, max);

    if (valueMin === valueMax) {
      return `${title}: ${valueMin}`;
    }
    return `${title}: ${valueMin} - ${valueMax}`;
  }

  if (isDateWithRangesValueV2(value) || isAvailabilityRuleV2(value)) {
    const minDate = formatDate(moment.min(value.ranges.map((range) => moment(range.start))));
    const maxDate = formatDate(moment.max(value.ranges.map((range) => moment(range.end))));

    return `${minDate} - ${maxDate}`;
  }

  if (isDateWithSingleRangeValueV2(value)) {
    return `${formatDate(value.start)} - ${formatDate(value.end)}`;
  }

  if (isOptionsFilterV2(filter)) {
    const values = Array.isArray(value) ? value : [value];
    return compact(
      values.map(
        (value) =>
          (isFilterValueOptionV2(value) ? value : (find(filter.options, { id: value }) as FilterOptionV2))
            ?.text
      )
    ).join(", ");
  }

  if (Array.isArray(value)) {
    const displayValue = compact(
      value.map(
        (valueItem) => (isFilterValueOptionV2(valueItem) ? valueItem?.text : valueItem.toString()) ?? ""
      )
    );
    return !isEmpty(displayValue) ? displayValue.join(", ") : "";
  }

  if (isFilterValueOptionV2(value)) {
    return value.text || "";
  }

  if (value === true || value === false || typeof value === "number") {
    return value.toString() ? value.toString() : "";
  }

  return value;
};
